import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Sidebar, PageHeader, MetricCard, formatCurrency } from "@/components/Layout";
import { useAuth, API } from "@/App";
import axios from "axios";
import { 
  Wallet, 
  TrendingDown, 
  Calendar, 
  PiggyBank,
  AlertTriangle,
  ArrowRight,
  Sparkles,
  Target,
  Receipt
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import SmartBudgetPanel from "@/components/SmartBudgetPanel";

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [goals, setGoals] = useState([]);
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, goalsRes, billsRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
          axios.get(`${API}/goals`, { withCredentials: true }),
          axios.get(`${API}/bills`, { withCredentials: true })
        ]);
        setStats(statsRes.data);
        setGoals(goalsRes.data.slice(0, 3));
        setBills(billsRes.data.slice(0, 5));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const currency = user?.currency || stats?.currency || 'GBP';

  if (loading) {
    return (
      <Sidebar>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-p2p-gold"></div>
        </div>
      </Sidebar>
    );
  }

  // Calculate realistic health status based on actual financial data
  const getHealthStatus = () => {
    const hasDebt = (stats?.total_debt || 0) > 0;
    const hasShortfall = stats?.risk_alerts?.some(a => a.type === 'critical');
    const hasTightMonth = stats?.risk_alerts?.some(a => a.type === 'warning');
    const billsCovered = !hasShortfall;
    
    if (hasShortfall) {
      return {
        label: 'Needs attention',
        sublabel: 'Potential shortfall ahead',
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        iconBg: 'bg-red-100',
        reasons: [
          hasShortfall && 'Upcoming bills may exceed balance',
          hasDebt && 'Active debt requiring payments'
        ].filter(Boolean)
      };
    } else if (hasTightMonth || (hasDebt && billsCovered)) {
      return {
        label: 'Tight but manageable',
        sublabel: 'Bills covered, debt active',
        color: 'text-amber-600',
        bgColor: 'bg-amber-50',
        borderColor: 'border-amber-200',
        iconBg: 'bg-amber-100',
        reasons: [
          billsCovered && 'Bills are covered this week',
          hasDebt && 'Debt is still active',
          hasTightMonth && 'Some months may be tight'
        ].filter(Boolean)
      };
    } else if (!hasDebt && billsCovered) {
      return {
        label: 'On track',
        sublabel: 'No debt, bills covered',
        color: 'text-emerald-600',
        bgColor: 'bg-emerald-50',
        borderColor: 'border-emerald-200',
        iconBg: 'bg-emerald-100',
        reasons: [
          'No active debt',
          'Bills are covered',
          'Forecast looks stable'
        ]
      };
    } else {
      return {
        label: 'Managing',
        sublabel: 'Making progress',
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200',
        iconBg: 'bg-blue-100',
        reasons: [
          billsCovered && 'Bills are covered',
          hasDebt && 'Working on debt reduction'
        ].filter(Boolean)
      };
    }
  };
  
  const healthStatus = getHealthStatus();

  return (
    <Sidebar>
      <PageHeader 
        title={`Good ${getTimeOfDay()}, ${user?.name?.split(' ')[0] || 'there'}!`}
        subtitle="Here's your financial overview"
      />

      {/* Bento Grid Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-8">
        {/* Financial Health Status - Takes 2 columns */}
        <div className={`p-6 rounded-2xl md:col-span-2 lg:row-span-2 border-2 ${healthStatus.bgColor} ${healthStatus.borderColor}`} data-testid="health-status-card">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-manrope text-lg font-semibold text-p2p-teal">Financial Health</h3>
          </div>
          
          {/* Status Display */}
          <div className="flex items-center gap-4 mb-6">
            <div className={`w-16 h-16 rounded-2xl ${healthStatus.iconBg} flex items-center justify-center`}>
              {healthStatus.label === 'On track' ? (
                <Sparkles className={`w-8 h-8 ${healthStatus.color}`} />
              ) : healthStatus.label === 'Needs attention' ? (
                <AlertTriangle className={`w-8 h-8 ${healthStatus.color}`} />
              ) : (
                <Target className={`w-8 h-8 ${healthStatus.color}`} />
              )}
            </div>
            <div>
              <p className={`text-2xl font-bold ${healthStatus.color}`}>{healthStatus.label}</p>
              <p className="text-sm text-p2p-muted">{healthStatus.sublabel}</p>
            </div>
          </div>
          
          {/* Reasons */}
          <div className="space-y-2 mb-6">
            <p className="text-xs font-medium text-p2p-muted uppercase tracking-wider">Why this status?</p>
            {healthStatus.reasons.map((reason, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className={`w-1.5 h-1.5 rounded-full ${healthStatus.color.replace('text-', 'bg-')}`} />
                <p className="text-sm text-p2p-muted">{reason}</p>
              </div>
            ))}
          </div>

          {/* Risk Alerts */}
          {stats?.risk_alerts?.length > 0 && (
            <div className="space-y-2">
              {stats.risk_alerts.map((alert, index) => (
                <div 
                  key={index}
                  className={`flex items-start gap-3 p-3 rounded-xl bg-white/50 ${
                    alert.type === 'critical' ? 'border border-red-200' : 
                    'border border-amber-200'
                  }`}
                  data-testid={`alert-${index}`}
                >
                  <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${
                    alert.type === 'critical' ? 'text-red-500' : 'text-amber-500'
                  }`} />
                  <div>
                    <p className="text-sm font-medium text-p2p-teal">{alert.message}</p>
                    <p className="text-xs text-p2p-muted">{alert.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Total Debt */}
        <MetricCard 
          title="Total Debt"
          value={formatCurrency(stats?.total_debt || 0, currency)}
          icon={TrendingDown}
          trend={stats?.total_debt > 0 ? 'down' : null}
        />

        {/* Monthly Expenses */}
        <MetricCard 
          title="Monthly Expenses"
          value={formatCurrency(stats?.monthly_expenses || 0, currency)}
          icon={Receipt}
        />

        {/* Upcoming Payments */}
        <MetricCard 
          title="Due Next 7 Days"
          value={formatCurrency(stats?.upcoming_payments_7d || 0, currency)}
          icon={Calendar}
        />

        {/* Disposable Income */}
        <MetricCard 
          title="Disposable Income"
          value={formatCurrency(stats?.disposable_income || 0, currency)}
          icon={PiggyBank}
          trend={stats?.disposable_income > 0 ? 'up' : stats?.disposable_income < 0 ? 'down' : null}
        />
      </div>

      {/* Smart Budget Panel */}
      <div className="mb-8">
        <SmartBudgetPanel />
      </div>

      {/* Goals and Bills Grid */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Goals Progress */}
        <div className="glass-card p-6 rounded-2xl" data-testid="goals-section">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-outfit text-lg font-semibold text-p2p-gray">Your Goals</h3>
            <Link to="/goals">
              <Button variant="ghost" size="sm" className="text-p2p-gold hover:bg-p2p-gold/10">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>

          {goals.length === 0 ? (
            <div className="text-center py-8">
              <Target className="w-12 h-12 text-p2p-muted mx-auto mb-4" />
              <p className="text-p2p-muted mb-4">No goals set yet</p>
              <Link to="/goals">
                <Button className="btn-gold rounded-full" data-testid="add-goal-btn">
                  Set Your First Goal
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {goals.map((goal) => (
                <div key={goal.goal_id} className="p-4 rounded-xl bg-white/5" data-testid={`goal-${goal.goal_id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-p2p-gray">{goal.name}</span>
                    <span className="text-sm text-p2p-gold">{goal.progress_percent}%</span>
                  </div>
                  <Progress value={goal.progress_percent} className="h-2 bg-white/10" />
                  <div className="flex justify-between mt-2 text-xs text-p2p-muted">
                    <span>{formatCurrency(goal.current_amount, currency)}</span>
                    <span>{formatCurrency(goal.target_amount, currency)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Upcoming Bills */}
        <div className="glass-card p-6 rounded-2xl" data-testid="bills-section">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-outfit text-lg font-semibold text-p2p-gray">Upcoming Bills</h3>
            <Link to="/bills">
              <Button variant="ghost" size="sm" className="text-p2p-gold hover:bg-p2p-gold/10">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>

          {bills.length === 0 ? (
            <div className="text-center py-8">
              <Receipt className="w-12 h-12 text-p2p-muted mx-auto mb-4" />
              <p className="text-p2p-muted mb-4">No bills added yet</p>
              <Link to="/bills">
                <Button className="btn-gold rounded-full" data-testid="add-bill-btn">
                  Add Your First Bill
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {bills.map((bill) => (
                <div 
                  key={bill.bill_id} 
                  className="flex items-center justify-between p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
                  data-testid={`bill-${bill.bill_id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      bill.is_debt ? 'bg-p2p-error/20' : 'bg-p2p-teal-light/30'
                    }`}>
                      <Receipt className={`w-5 h-5 ${bill.is_debt ? 'text-p2p-error' : 'text-p2p-teal-light'}`} />
                    </div>
                    <div>
                      <p className="font-medium text-p2p-gray">{bill.name}</p>
                      <p className="text-xs text-p2p-muted capitalize">{bill.frequency}</p>
                    </div>
                  </div>
                  <span className="font-mono font-medium text-p2p-gray">
                    {formatCurrency(bill.amount, currency)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* AI Insights CTA */}
      {!user?.is_premium && (
        <div className="mt-8 glass-card p-6 rounded-2xl bg-gradient-to-r from-p2p-gold/10 to-transparent border border-p2p-gold/20" data-testid="premium-cta">
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-p2p-gold/20 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-6 h-6 text-p2p-gold" />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <h3 className="font-outfit text-lg font-semibold text-p2p-gray mb-1">
                Unlock AI-Powered Insights
              </h3>
              <p className="text-sm text-p2p-muted">
                Get personalized recommendations to pay off debt faster and optimize your spending
              </p>
            </div>
            <Link to="/settings">
              <Button className="btn-gold rounded-full" data-testid="upgrade-btn">
                Upgrade to Premium
              </Button>
            </Link>
          </div>
        </div>
      )}
    </Sidebar>
  );
}

function getTimeOfDay() {
  const hour = new Date().getHours();
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
}
